import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Input, Row, Space, Switch, Tag, Typography } from "antd";
import * as actions from "../api/Features";
import { Props } from "../api/Features";

const { Text } = Typography;

const getMarketplaceGroup = (name: string): string => {
  if (name.startsWith("ya_")) return "Yandex";
  if (name.startsWith("sber_")) return "Sber";
  if (name.startsWith("ali_")) return "AliExpress";
  if (name.startsWith("ozon_")) return "Ozon";
  if (name.startsWith("wb_")) return "Wildberries";
  return "Other";
};

const GROUP_ORDER = ["Yandex", "Sber", "AliExpress", "Ozon", "Wildberries", "Other"];

const FeatureFlags = () => {
  const [values, setValues] = useState<Props[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [editable, setEditable] = useState(false);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await actions.getAllFeatureFlags();
      setValues(result);
    } catch {
      setError("Не удалось загрузить feature flags.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const update = useCallback(
    async (name: string, value: boolean) => {
      const prev = values;
      setSavingName(name);
      setError(null);
      setValues((current) =>
        current.map((el) => (el.name === name ? { ...el, value } : el))
      );
      try {
        await actions.updateFeatureFlagsValue(name, value);
      } catch {
        setValues(prev);
        setError(`Не удалось обновить флаг ${name}.`);
      } finally {
        setSavingName(null);
      }
    },
    [values]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return values;
    return values.filter(
      (el) =>
        el.translate.toLowerCase().includes(q) || el.name.toLowerCase().includes(q)
    );
  }, [query, values]);

  const grouped = useMemo(() => {
    const map = new Map<string, Props[]>();
    for (const item of filtered) {
      const group = getMarketplaceGroup(item.name);
      const current = map.get(group) || [];
      current.push(item);
      map.set(group, current);
    }
    return GROUP_ORDER.filter((group) => map.has(group)).map((group) => ({
      group,
      items: map.get(group) || [],
    }));
  }, [filtered]);

  return (
    <Card
      title="Feature Flags"
      extra={
        <Space>
          <Button onClick={loadFlags} loading={loading}>
            Обновить
          </Button>
          <Space>
            <Text strong>Режим редактирования</Text>
            <Switch checked={editable} onChange={setEditable} />
          </Space>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        {error && <Alert type="error" showIcon message={error} />}

        <Alert
          type={editable ? "warning" : "info"}
          showIcon
          message={
            editable
              ? "Редактирование включено. Изменения применяются сразу при переключении."
              : "Редактирование выключено. Переключатели заблокированы от случайных кликов."
          }
        />

        <Input
          allowClear
          placeholder="Поиск по названию или ключу флага"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {grouped.map((section) => (
          <div key={section.group}>
            <div
              style={{
                borderBottom: "1px solid #f0f0f0",
                margin: "8px 0 12px",
                paddingBottom: 6,
              }}
            >
              <Text strong>{section.group}</Text>
            </div>
            <Row gutter={[12, 12]}>
              {section.items.map((el) => (
                <Col xs={24} md={12} lg={8} key={el.name}>
                  <Card size="small">
                    <Space
                      align="start"
                      style={{ width: "100%", justifyContent: "space-between" }}
                    >
                      <div>
                        <div>{el.translate}</div>
                        <Text type="secondary">{el.name}</Text>
                        <div style={{ marginTop: 8 }}>
                          <Tag color={el.value ? "green" : "default"}>
                            {el.value ? "ON" : "OFF"}
                          </Tag>
                        </div>
                      </div>
                      <Switch
                        checked={el.value}
                        disabled={!editable || loading}
                        loading={savingName === el.name}
                        onChange={(checked) => update(el.name, checked)}
                      />
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </Space>
    </Card>
  );
};

export default FeatureFlags;
